// src/components/StudioPackagesPage.js
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { readBookingReturnFromSearch } from "../utils/bookingReturnUtils";
import SEO from "./SEO";
import { FEATURES } from "../config";
import "./StudioPackagesPage.css";

const getEffectiveMetadata = (product) => ({
  ...(product.metadata || {}),
  ...(product.price?.metadata || {}),
});

const isUnifiedSharedPackage = (product) => {
  const metadata = getEffectiveMetadata(product);
  return (
    metadata.creditModel === "unified_shared" &&
    (metadata.productType === "package" ||
      metadata.creditCategory === "package")
  );
};

const isLegacyPackage = (product) => {
  const metadata = getEffectiveMetadata(product);
  return (
    metadata.productType === "red-light-package" ||
    metadata.productType === "sauna-package" ||
    metadata.productType === "combo-package"
  );
};

const getPackageSortValue = (product) => {
  const metadata = getEffectiveMetadata(product);
  const numericValue = Number(metadata.totalSessions || metadata.sessionCredits);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : Number.MAX_SAFE_INTEGER;
};

const StudioPackagesPage = () => {
  const [studioPackages, setStudioPackages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const { addItem, setBookingReturn } = useShoppingCart();
  const location = useLocation();

  // ADR-027 §A1.4(a): lift the booking-return context off the URL params into
  // the in-memory cart carrier (mirror of DropInsPage). CartPage forwards it
  // to the backend as `bookingReturn` so the deployed success_url round-trips
  // it across the external Stripe redirect. Replaces the removed legacy
  // browser-storage stash (the R-020 vector). Only BE-valid values are stored
  // (BE fail-closed contract).
  useEffect(() => {
    const ctx = readBookingReturnFromSearch(location.search);
    if (ctx) setBookingReturn(ctx);
  }, [location.search, setBookingReturn]);

  useEffect(() => {
    setLoading(true);
    const fetchStudioPackages = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/products`
        );
        const products = await response.json();
        const unifiedPackages = products.filter(isUnifiedSharedPackage);

        if (FEATURES.ENABLE_UNIFIED_STOREFRONT && unifiedPackages.length > 0) {
          const sortedUnifiedPackages = [...unifiedPackages].sort(
            (a, b) =>
              getPackageSortValue(a) - getPackageSortValue(b) ||
              a.name.localeCompare(b.name)
          );
          setStudioPackages(sortedUnifiedPackages);
          return;
        }

        const filteredPackages = products.filter(isLegacyPackage);

        const saunaPackages = filteredPackages.filter((pkg) => {
          const metadata = getEffectiveMetadata(pkg);
          return metadata.productType === "sauna-package";
        });
        const redLightPackages = filteredPackages.filter((pkg) => {
          const metadata = getEffectiveMetadata(pkg);
          return metadata.productType === "red-light-package";
        });
        const comboPackages = filteredPackages.filter((pkg) => {
          const metadata = getEffectiveMetadata(pkg);
          return metadata.productType === "combo-package";
        });

        const sortByPriceDesc = (a, b) => {
          const priceA = a.price ? a.price.unit_amount : 0;
          const priceB = b.price ? b.price.unit_amount : 0;
          return priceB - priceA;
        };
        saunaPackages.sort(sortByPriceDesc);
        redLightPackages.sort(sortByPriceDesc);
        comboPackages.sort(sortByPriceDesc);

        setStudioPackages([
          ...saunaPackages,
          ...redLightPackages,
          ...comboPackages,
        ]);
      } catch (error) {
        console.error("Error fetching studio packages:", error);
        setMessage("Failed to load studio packages. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudioPackages();
  }, []);

  return (
    <div className="studio-packages-page">
      <SEO
        title="Studio Packages | Diviti Adora Wellness Studio | Oro Valley AZ"
        description="Choose from our studio packages for infrared sauna and red light therapy sessions at Diviti Adora in Oro Valley. Sauna packages, red light packages, and combo packages available."
        keywords="studio packages, infrared sauna packages, red light therapy packages, combo packages, wellness packages, Oro Valley wellness, sauna sessions, red light sessions, infrared sauna Oro Valley AZ, red light therapy near Oro Valley, packages for red light therapy Tucson, Tucson infrared sauna sessions, full spectrum infrared sauna Oro Valley, LED red light therapy facials Tucson, Southern Arizona wellness retreats"
        canonicalUrl="/studio-packages"
      />
      <header className="studio-packages-header">
        <h1>Studio Packages</h1>
        <p>
          Choose from our studio packages for flexible shared sauna and red
          light sessions.
        </p>
        {/*<div className="promo-code-banner">
          <h3>Limited Time Offer: 15% OFF</h3>
          <p>
            Use promo code <span className="promo-code">RelaxRefresh15</span> at
            checkout for 15% off any membership or studio pacakage purchased
            before April 30, 2025.
          </p>
        </div>*/}
      </header>
      <section className="studio-packages-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading studio packages...</p>
          </div>
        ) : studioPackages.length === 0 ? (
          <p>No studio packages available at the moment.</p>
        ) : (
          <div className="package-cards">
            {studioPackages.map((product) => {
              const metadata = getEffectiveMetadata(product);

              return (
                <div key={product.id} className="package-card">
                {/* show the product’s primary image if Stripe returned one */}
                {product.images?.length > 0 && (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="package-card-image"
                  />
                )}
                <h2>{product.name}</h2>
                <p>{product.description}</p>
                {product.metadata && product.metadata.sku && (
                  <p>SKU: {metadata.sku}</p>
                )}
                {product.price && product.price.unit_amount ? (
                  <p>
                    Price: ${(product.price.unit_amount / 100).toFixed(2)}{" "}
                    {product.price.currency.toUpperCase()}
                  </p>
                ) : (
                  <p>Price not available</p>
                )}
                {metadata.totalSessions && (
                  <p>Sessions Included: {metadata.totalSessions}</p>
                )}
                <button
                  className="add-to-cart-btn"
                  onClick={() => {
                    addItem({
                      id: product.id,
                      priceId: product.price.id, // Add the priceId property
                      name: product.name,
                      price: product.price.unit_amount,
                      currency: product.price.currency,
                      type: "package",
                      productType: metadata.productType || "package",
                      creditModel: metadata.creditModel || null,
                      creditCategory: metadata.creditCategory || "package",
                      serviceAccess: metadata.serviceAccess || null,
                      sessionCredits:
                        Number(metadata.sessionCredits || metadata.totalSessions) || 0,
                      totalSessions:
                        Number(metadata.totalSessions || metadata.sessionCredits) || 0,
                      quantity: 1,
                    });
                    setMessage(`${product.name} added to cart!`);
                    setTimeout(() => setMessage(""), 3000);
                  }}
                >
                  Add to Cart
                </button>
                </div>
              );
            })}
          </div>
        )}
        {message && <p className="message">{message}</p>}
      </section>
    </div>
  );
};

export default StudioPackagesPage;
